import React, { useEffect, useState } from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useInfiniteScroll, useRequest } from 'ahooks';
import { Spin } from 'antd';
import Footer from '@/business-components/footer';
import Layout from '@/layouts';
import SortMenu from '@/business-components/SortMenu';
import MyBrandListEmptyState from '@/business-components/MyBrandListEmptyState';
import MyBrandCard from '@/business-components/MyBrandCard';
import { ConUserBrandWebController } from '@/services/swagger/ConUserBrandWebController';
import styles from './index.module.scss';

const userBrandController = new ConUserBrandWebController();
const pageSize = 16;

export type IndexPageServiderProps = Record<string, null>;

export const getServerSideProps: GetServerSideProps<IndexPageServiderProps> = async ({ res }) => {
  res.setHeader('Cache-Control', 'public, s-maxage=15, stale-while-revalidate=59');
  return { props: {} };
};

function MyBrandPage({}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState('first_word_asc');
  const { data: authData = { list: [] } } = useRequest(async () => {
    const r = await userBrandController.conUserBrandWebControllerV1UserAuthBrandUsingGet1();
    return {
      list: r.data.data?.list || [],
      total: r.data.data?.total || 0,
    };
  });
  const {
    data = { list: [] },
    // loading,
    loadMore,
    loadingMore,
    reload,
  } = useInfiniteScroll(
    async d => {
      const r = await userBrandController.conUserBrandWebControllerV1ListUsingGet1({
        orderType,
        pageIndex: Math.floor((d?.list?.length || 0) / pageSize) + 1 || 1,
        pageSize,
      });
      return {
        list: r.data.data?.list || [],
        total: r.data.data?.total || 0,
      };
    },
    {
      reloadDeps: [orderType],
      isNoMore: d => {
        return d ? d.list.length >= d.total : false;
      },
      onFinally() {
        setLoading(false);
      },
    }
  );
  // console.log([...authData.list, ...data.list]);
  const handleScroll = (e: Event) => {
    if (loadingMore) {
      return;
    }
    const scrollTop =
      (e.target && (e.target as Document).documentElement.scrollTop) ||
      window.scrollY ||
      (e.target && (e.target as Document).body.scrollTop) ||
      0;
    const scrollHeight =
      (e.target && (e.target as Document).documentElement.scrollHeight) ||
      (e.target && (e.target as Document).body.scrollHeight) ||
      0;
    const windowHeight =
      document.compatMode === 'CSS1Compat' ? document.documentElement.clientHeight : document.body.clientHeight;
    if (scrollHeight - scrollTop - windowHeight < 500) {
      loadMore();
    }
  };
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });
  useEffect(() => {
    setLoading(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    setTimeout(() => {
      reload();
    }, 1000);
  }, [reload]);
  return (
    <>
      <Layout
        title="我的品牌库 | CBNData品牌数集"
        description="我的品牌库 | CBNData品牌数集是第一财经商业数据中心（CBNData）旗下一款衡量品牌综合实力的可视化数据工具，基于CBNData沉淀的中国新消费品牌增长力评估模型与数据能力，从品牌赛道潜力、组织能力、经营能力、产品竞争力、营销能力、供应链能力、用户发展能力等7个维度，系统化帮助投资人及品牌创始人衡量品牌综合实力、评估品牌风险、分析行业竞争格局以及挖掘高增长潜力的新锐品牌。"
        keyword="品牌、数据库、品牌库、品牌列表、消费、新消费、行业、赛道、融资轮次、成立时间、标签、品牌数集、CBNData、CBN、第一财经"
        isShowNavBar
        isFrapMain
        FooterComponent={Footer}
        mainClass={styles.layout}
      >
        <div className={styles.sortMenuContainer}>
          <SortMenu
            className={styles.menu}
            title={
              <div className={styles.title}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M24 0H8C3.58172 0 0 3.58172 0 8V24C0 28.4183 3.58172 32 8 32H24C28.4183 32 32 28.4183 32 24V8C32 3.58172 28.4183 0 24 0Z"
                    fill="#F75C2F"
                  />
                  <path
                    d="M15.1224 15.445H11.2225C8.90125 15.445 7 13.5458 7 11.2225C7 8.90125 8.89922 7 11.2225 7C13.5438 7 15.445 8.89922 15.445 11.2225V15.1224C15.443 15.301 15.299 15.445 15.1224 15.445ZM20.7754 15.445H16.8756C16.699 15.445 16.555 15.301 16.555 15.1245V11.2246C16.555 8.90328 18.4542 7.00203 20.7775 7.00203C23.0987 7.00203 25 8.90125 25 11.2246C24.998 13.5458 23.0987 15.445 20.7754 15.445ZM11.2225 25C8.90125 25 7 23.1008 7 20.7775C7 18.4562 8.89922 16.555 11.2225 16.555H15.1224C15.299 16.555 15.443 16.699 15.443 16.8755V20.7754C15.443 23.1008 13.5438 25 11.2225 25ZM20.7754 25C18.4542 25 16.5529 23.1008 16.5529 20.7775V16.8776C16.5529 16.701 16.697 16.557 16.8735 16.557H20.7734C23.0947 16.557 24.9959 18.4562 24.9959 20.7795C24.998 23.1008 23.0987 25 20.7754 25Z"
                    fill="white"
                  />
                </svg>
                <span className={styles.titleText}>我的品牌库</span>
              </div>
            }
            selectedLabelValue={orderType}
            onSortLabelClick={v => {
              setOrderType(v);
            }}
          />
        </div>
        <Spin spinning={loading}>
          <section
            className={!data?.list?.length && !authData?.list?.length ? styles.galleryEmptyState : styles.gallery}
          >
            {!data?.list?.length && !authData?.list?.length && <MyBrandListEmptyState />}
            {[...authData?.list, ...data?.list].map(item => {
              return <MyBrandCard className={styles.cardContainer} key={item.conBrandId} {...item} />;
            })}
          </section>
        </Spin>
      </Layout>
    </>
  );
}

export default MyBrandPage;
